import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check } from 'lucide-react';
import { Seo } from '../components/Seo';
import { BUSINESS, SEASON } from '../data/site';
import { CHECKOUT, CANCEL_NOTICE, SIBLING_CODE } from '../data/checkout';

/**
 * Private enrollment page. Not in the nav, not in the sitemap, noindex.
 * Send this URL to a family after their evaluation so they can enroll in the plan you agreed on.
 */

interface Offer {
  freq: string;
  name: string;
  who?: string;
  price: string;
  per: string;
  bullets: string[];
  url: string;
  cta: string;
  hero?: boolean;
}
const GROUP: Offer[] = [{
  freq: '1x / week',
  name: 'Advanced Performance',
  who: 'High School · Ages 14+ · Every sport',
  price: '$180',
  per: '/ month',
  bullets: ['4 sessions per month', '75-minute sessions', 'Tuesday or Thursday, 5:45–7:00 PM'],
  url: CHECKOUT.advanced1x,
  cta: 'Enroll · $180/mo'
}, {
  freq: '2x / week',
  name: 'Advanced Performance',
  who: 'High School · Ages 14+ · Every sport',
  price: '$325',
  per: '/ month',
  bullets: ['8 sessions per month', '75-minute sessions', 'Tuesday and Thursday, 5:45–7:00 PM'],
  url: CHECKOUT.advanced2x,
  cta: 'Enroll · $325/mo',
  hero: true
}, {
  freq: '1x / week',
  name: 'Homeschool Multisport',
  who: 'Homeschool athletes · Capped at 10',
  price: '$225',
  per: '/ month',
  bullets: ['90-minute sessions', 'Tuesday and Thursday, 2:30–4:00 PM', 'Mon 10:00 AM · Wed 12:00 PM starts'],
  url: CHECKOUT.homeschool1x,
  cta: 'Enroll · $225/mo'
}, {
  freq: '2x / week',
  name: 'Homeschool Multisport',
  who: 'Homeschool athletes · Capped at 10',
  price: '$400',
  per: '/ month',
  bullets: ['90-minute sessions', 'Tuesday and Thursday, 2:30–4:00 PM', 'Mon 10:00 AM · Wed 12:00 PM starts'],
  url: CHECKOUT.homeschool2x,
  cta: 'Enroll · $400/mo'
}];
const PRIVATE: Offer[] = [{
  freq: 'Base rate · 1x / week',
  name: 'Private 1-on-1',
  price: '$600',
  per: '/ month',
  bullets: ['4 sessions per month', '$150 per session', 'Individualized program'],
  url: CHECKOUT.private1x,
  cta: 'Enroll · $600/mo'
}, {
  freq: 'Save 10% · 2x / week',
  name: 'Private 1-on-1',
  price: '$1,080',
  per: '/ month',
  bullets: ['8 sessions per month', '$135 per session · saves $120/mo', 'Individualized program'],
  url: CHECKOUT.private2x,
  cta: 'Enroll · $1,080/mo',
  hero: true
}, {
  freq: 'Save 15% · 3x / week',
  name: 'Private 1-on-1',
  price: '$1,530',
  per: '/ month',
  bullets: ['12 sessions per month', '$127.50 per session · saves $270/mo', 'Individualized program'],
  url: CHECKOUT.private3x,
  cta: 'Enroll · $1,530/mo'
}];
const SEMI: Offer[] = [{
  freq: 'Base rate · 1x / week',
  name: 'Semi-Private 2-on-1',
  who: 'Per athlete · each athlete enrolls separately',
  price: '$500',
  per: '/ month per athlete',
  bullets: ['4 sessions per month', '$125 per session', 'Sibling discount stacks'],
  url: CHECKOUT.semi1x,
  cta: 'Enroll · $500/mo'
}, {
  freq: 'Save 10% · 2x / week',
  name: 'Semi-Private 2-on-1',
  who: 'Per athlete · each athlete enrolls separately',
  price: '$900',
  per: '/ month per athlete',
  bullets: ['8 sessions per month', '$112.50 per session', 'Sibling discount stacks'],
  url: CHECKOUT.semi2x,
  cta: 'Enroll · $900/mo'
}, {
  freq: 'Save 15% · 3x / week',
  name: 'Semi-Private 2-on-1',
  who: 'Per athlete · each athlete enrolls separately',
  price: '$1,275',
  per: '/ month per athlete',
  bullets: ['12 sessions per month', '$106.25 per session', 'Sibling discount stacks'],
  url: CHECKOUT.semi3x,
  cta: 'Enroll · $1,275/mo'
}];
const ONE_TIME: Offer[] = [{
  freq: 'Single session',
  name: 'Drop-In',
  price: '$50',
  per: '/ session',
  bullets: ['Try a group session', 'No commitment', 'Reserve the same week'],
  url: CHECKOUT.dropIn,
  cta: 'Buy · $50'
}, {
  freq: '10 sessions',
  name: '10-Session Card',
  price: '$450',
  per: 'total',
  bullets: [`Use through ${SEASON.end}`, 'Any group session', 'Share within family'],
  url: CHECKOUT.card10,
  cta: 'Buy · $450'
}, {
  freq: 'Sept 12 + 26 · Saturdays 9:30 AM',
  name: 'Speed School',
  who: '15 athletes max',
  price: '$125',
  per: 'both sessions',
  bullets: ['Two Saturday sessions', 'Speed and force plate testing', 'Single session available at $75'],
  url: CHECKOUT.speedSchoolBoth,
  cta: 'Reserve · $125',
  hero: true
}];
export function Enroll() {
  return <div className="w-full" data-id="element-437" data-mp="2civohwq1ew2l">
      <Seo title="Complete Enrollment | Reach Performance" description="Choose your confirmed plan and set up billing." path="/enroll" noindex data-id="element-438" data-mp="27hy5xj14800k" />

      <section className="relative bg-reach-black overflow-hidden" data-id="element-439" data-mp="10z9gufak4xfs">
        <div className="absolute inset-0 pointer-events-none overflow-hidden" data-id="element-440" data-mp="w0y704k9imxk">
          <div className="absolute top-1/4 -left-40 w-[50%] h-1.5 bg-reach-red transform -rotate-[6deg] opacity-50" data-id="element-441" data-mp="lznikx2vwjsn" />
          <div className="absolute top-[42%] -left-40 w-[35%] h-1.5 bg-reach-red transform -rotate-[6deg] opacity-30" data-id="element-442" data-mp="qy75p0t7s1ml" />
        </div>
        <div className="max-w-container mx-auto px-4 md:px-8 py-16 md:py-20 relative z-10" data-id="element-443" data-mp="10ni7s3hoo60z">
          <p className="font-inter text-reach-red-light text-xs uppercase tracking-[0.3em] font-semibold mb-4" data-id="element-444" data-mp="10pbjhx75lgfu">
            Private enrollment page
          </p>
          <h1 className="font-bebas text-white text-6xl md:text-8xl tracking-wide leading-none mb-4" data-id="element-445" data-mp="19mvnhdwehqu5">
            Complete Enrollment
          </h1>
          <p className="font-inter text-white/85 text-base md:text-xl max-w-2xl" data-id="element-446" data-mp="1jmcqtoo6jsmy">
            Your evaluation is done and your plan is confirmed. Select it below to set up billing.
            Monthly plans renew automatically and can be cancelled anytime with {CANCEL_NOTICE}{' '}
            notice.
          </p>
        </div>
      </section>

      <Group kicker="Group Training" title="Monthly Group Memberships" offers={GROUP} data-id="element-447" data-mp="3ddt8yeiwelsl">
        <SiblingNote data-id="element-448" data-mp="38f02v75xxx2g" />
      </Group>
      <Group kicker="1-on-1" title="Private Training" offers={PRIVATE} band data-id="element-449" data-mp="38g1gnjeccxnv" />
      <Group kicker="2-on-1" title="Semi-Private Training" offers={SEMI} data-id="element-450" data-mp="3ncsidzbhmlgn" />
      <Group kicker="No subscription" title="One-Time Options" offers={ONE_TIME} band data-id="element-451" data-mp="3ibuzfkinlne3">
        <p className="font-inter text-sm text-reach-black/70 text-center mt-6" data-id="element-452" data-mp="5zu2bo5aph2">
          Speed School single session:{' '}
          <a href={CHECKOUT.speedSchoolSingle} className="font-semibold text-reach-black underline hover:text-reach-red" data-id="element-453" data-mp="3rc1az0uskdam">
            reserve one Saturday for $75
          </a>
          .
        </p>
      </Group>

      <section className="bg-white py-14" data-id="element-454" data-mp="4xqa8y9rk7ge">
        <div className="max-w-3xl mx-auto px-4 md:px-8 text-center" data-id="element-455" data-mp="iwq02990ise5">
          <p className="font-inter text-reach-red text-xs uppercase tracking-[0.3em] font-semibold mb-3" data-id="element-456" data-mp="iuwrupir4041">
            Questions before you pay?
          </p>
          <p className="font-inter text-base text-reach-black/80 leading-relaxed" data-id="element-457" data-mp="xkrew25zh741">
            Text or call{' '}
            <a href={BUSINESS.phoneHref} className="font-semibold underline" data-id="element-458" data-mp="12hhvqrpm46qi">
              {BUSINESS.phoneDisplay}
            </a>
            , or email{' '}
            <a href={`mailto:${BUSINESS.email}`} className="font-semibold underline" data-id="element-459" data-mp="thurly5i0mtr">
              {BUSINESS.email}
            </a>
            . Nothing is charged until you complete checkout. Update your card, pause, or cancel
            anytime from the link in your receipt email.
          </p>
          <div className="mt-8" data-id="element-460" data-mp="5ss8elv7ulx5">
            <Link to="/pricing" className="btn-secondary" data-id="element-461" data-mp="5ttm6y3m9mik">
              See full pricing and policies
            </Link>
          </div>
        </div>
      </section>
    </div>;
}
function SiblingNote() {
  return <div className="max-w-6xl mx-auto mt-6 border-l-4 border-reach-red bg-white p-5 md:p-6" data-id="element-462" data-mp="3s0ign3nb05a2">
      <p className="font-bebas text-lg tracking-wider uppercase mb-1" data-id="element-463" data-mp="sw92zxgrinx">Sibling discount</p>
      <p className="font-inter text-sm text-reach-black/80" data-id="element-464" data-mp="ga5ovldufkul">
        Enrolling a second athlete? Enter code{' '}
        <span className="font-semibold text-reach-black" data-id="element-465" data-mp="bd5ewzdvhxy7">{SIBLING_CODE}</span> at checkout for 10%
        off. It stacks on 2-on-1 packages when both athletes are siblings.
      </p>
    </div>;
}
function Group({
  kicker,
  title,
  offers,
  band = false,
  children
}: {
  kicker: string;
  title: string;
  offers: Offer[];
  band?: boolean;
  children?: React.ReactNode;
}) {
  return <section className={`${band ? 'bg-reach-gray' : 'bg-white'} py-14 md:py-16`} data-id="element-466" data-mp="3oppco4vqwb29">
      <div className="max-w-container mx-auto px-4 md:px-8" data-id="element-467" data-mp="3jt7938c5iiri">
        <div className="mb-8" data-id="element-468" data-mp="3fqu5apgysuxd">
          <p className="font-inter text-reach-red text-xs uppercase tracking-[0.3em] font-semibold mb-2" data-id="element-469" data-mp="3frlqr7gulc9x">
            {kicker}
          </p>
          <h2 className="font-bebas text-4xl md:text-5xl tracking-wide leading-none" data-id="element-470" data-mp="3atk5cfvy90va">{title}</h2>
        </div>
        <div className="grid gap-5 md:gap-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-3 max-w-6xl mx-auto" data-id="element-471" data-mp="35uqukv81rnyt">
          {offers.map(o => <OfferCard key={`${o.name}-${o.freq}`} offer={o} onBand={band} data-id="element-472" data-mp="1q8nhqwiv1bfl" />)}
        </div>
        {children}
      </div>
    </section>;
}
function OfferCard({
  offer,
  onBand
}: {
  offer: Offer;
  onBand: boolean;
}) {
  const isDark = !!offer.hero;
  const lightBg = onBand ? 'bg-white' : 'bg-reach-gray';
  return <div className={`relative rounded-[4px] p-6 md:p-7 flex flex-col overflow-hidden border-b-4 ${isDark ? 'bg-reach-black text-white ring-2 ring-reach-red border-transparent' : `${lightBg} text-reach-black border-transparent`}`} data-id="element-473" data-mp="12lfcgelfm2dk">
      <p className={`font-inter text-xs uppercase tracking-[0.25em] font-semibold mb-2 ${isDark ? 'text-reach-red-light' : 'text-reach-red'}`} data-id="element-474" data-mp="1gb92h08oasov">
        {offer.freq}
      </p>
      <h3 className="font-bebas text-2xl md:text-3xl tracking-wide leading-none mb-2" data-id="element-475" data-mp="jrjtg057mo14">{offer.name}</h3>
      {offer.who && <p className={`font-inter text-xs uppercase tracking-wider mb-5 ${isDark ? 'text-white/70' : 'text-reach-black/70'}`} data-id="element-476" data-mp="3pzp69q64y37p">
          {offer.who}
        </p>}
      <div className="mb-6 flex items-baseline gap-2 flex-wrap" data-id="element-477" data-mp="2upqxogvp3ja">
        <span className="font-bebas text-4xl md:text-5xl tracking-wide leading-none" data-id="element-478" data-mp="2sdbbhylf59e">{offer.price}</span>
        <span className={`font-inter text-sm ${isDark ? 'text-white/75' : 'text-reach-black/70'}`} data-id="element-479" data-mp="7rgfl06r17gb">
          {offer.per}
        </span>
      </div>
      <ul className="space-y-2.5 mb-7 flex-grow" data-id="element-480" data-mp="3b9hxk25xjyk7">
        {offer.bullets.map(b => <li key={b} className={`flex items-start gap-2.5 text-sm ${isDark ? 'text-white/85' : 'text-reach-black/85'}`} data-id="element-481" data-mp="3g8ug4aprqjgz">
            <Check size={16} className={`shrink-0 mt-0.5 ${isDark ? 'text-reach-red-light' : 'text-reach-red'}`} aria-hidden="true" data-id="element-482" data-mp="3l25sssh9hugt" />
            <span data-id="element-483" data-mp="2shyuxwftimip">{b}</span>
          </li>)}
      </ul>
      <a href={offer.url} className="btn-primary w-full" data-id="element-484" data-mp="2l0itdltnve38">
        {offer.cta}
        <ArrowRight size={16} className="ml-2" aria-hidden="true" data-id="element-485" data-mp="2g1foc1dol7v9" />
      </a>
    </div>;
}